import { useQuery } from '@apollo/client';
import { Notification, ProfileNavigation, UserInfo } from '@deltasierra/components';
import { Navigation } from '@deltasierra/features-left-navigation';
import { LeftNavigationView } from '../enums';
import { GetWhitelabelLogoForLeftNavigation } from '../queries/__graphqlTypes/GetWhitelabelLogoForLeftNavigation';
import { GET_WHITELABEL_LOGO_FOR_LEFT_NAVIGATION_QUERY } from '../queries/GetWhitelabelLogoForLeftNavigation.query';
import { useCurrentUrlPath } from './useCurrentUrlPath';
import { useLeftNavigationAccountMenu } from './useLeftNavigationAccountMenu';
import { useLeftNavigationClientLocation } from './useLeftNavigationClientLocation';
import { useLeftNavigationDrawerPreference } from './useLeftNavigationDrawerPreference';
import { useLeftNavigationMenu } from './useLeftNavigationMenu';
import { useLeftNavigationNotifications } from './useLeftNavigationNotifications';
import { useNavigationSwitchView } from './useNavigationSwitchView';
import { useUpdateLeftNavigationBodyClasses } from './useUpdateLeftNavigationBodyClasses';

type UseLeftNavigationReturn = {
    currentUrlPath: string;
    currentView: LeftNavigationView;
    homeHref: string;
    isDrawerOpen: boolean;
    isLoadingDrawerPreference: boolean;
    isLoadingNavigationMenu: boolean;
    isLoadingNotifications: boolean;
    logoImageSrc: string | null;
    logoLoading: boolean;
    navigation: Navigation<React.ReactNode>;
    notifications: Notification[];
    onClickDismissAllNotifications: () => void;
    onClickDismissNotification: (id: string) => void;
    onDrawerToggle: () => Promise<void>;
    onToggleView: (view: LeftNavigationView) => void;
    profileNavigation: ProfileNavigation;
    userInfo: UserInfo;
};

export function useLeftNavigation(): UseLeftNavigationReturn {
    const currentUrlPath = useCurrentUrlPath();

    const { clientId, locationId } = useLeftNavigationClientLocation();

    const { currentView, onToggleView } = useNavigationSwitchView({ currentUrlPath });

    const {
        homeHref,
        isLoading: isLoadingNavigationMenu,
        navigation,
    } = useLeftNavigationMenu({
        clientId,
        currentUrlPath,
        currentView,
        locationId,
    });

    const { profileNavigation, userInfo } = useLeftNavigationAccountMenu();

    const { isLoadingNotifications, notifications, onClickDismissAllNotifications, onClickDismissNotification } =
        useLeftNavigationNotifications();

    const { isDrawerOpen, loading: isLoadingDrawerPreference, onDrawerToggle } = useLeftNavigationDrawerPreference();

    const { data: whitelabelLogoData, loading: logoLoading } = useQuery<GetWhitelabelLogoForLeftNavigation>(
        GET_WHITELABEL_LOGO_FOR_LEFT_NAVIGATION_QUERY,
    );
    const logoImageSrc: string | null = whitelabelLogoData?.me.whitelabelLogo?.signedUrl ?? null;

    useUpdateLeftNavigationBodyClasses({ isDrawerOpen });

    return {
        currentUrlPath,
        currentView,
        homeHref,
        isDrawerOpen,
        isLoadingDrawerPreference,
        isLoadingNavigationMenu,
        isLoadingNotifications,
        logoImageSrc,
        logoLoading,
        navigation,
        notifications,
        onClickDismissAllNotifications,
        onClickDismissNotification,
        onDrawerToggle,
        onToggleView,
        profileNavigation,
        userInfo,
    };
}
